import CopyableId from "@/components/common/CopyableId";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Siren, AlertTriangle, HeartPulse, Phone, Check, Play, ArrowUpRight,
  ExternalLink, Clock, BellOff, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import SosDialog from "@/components/common/SosDialog";

/**
 * Global Critical Case popup. Polls /critical/active-cases every 12s for
 * unhandled critical RPM alerts + open critical/STAT hotline sessions. Shows a
 * centered modal stacking every active case, each with its intervention actions
 * + SOS. Cases can be snoozed 10 min (re-appear after); handled cases drop off.
 */
const POLL_MS = 12_000;
const SNOOZE_MS = 10 * 60_000;
const REDISPLAY_MS = 30_000; // after close, re-pop in 30s while cases remain unhandled
const CLINICAL = ["physician", "hah_physician", "nurse", "hah_nurse", "hah_coordinator", "care_coordinator", "admin"];

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function CriticalCaseProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isClinical = user && CLINICAL.includes(user.role_code);

  const [cases, setCases] = useState([]);
  const [snoozed, setSnoozed] = useState({}); // key -> until-ms
  const [dismissedOpen, setDismissedOpen] = useState(false); // user closed the modal this cycle
  const [sos, setSos] = useState({ open: false, context: null });
  const [actionMode, setActionMode] = useState({}); // key -> { mode, note }
  const [busyKey, setBusyKey] = useState(null);
  const prevCount = useRef(0);

  const fetchCases = useCallback(async () => {
    if (!isClinical) return;
    try {
      const r = await api.get("/critical/active-cases");
      setCases(r.data.items || []);
    } catch { /* silent */ }
  }, [isClinical]);

  useEffect(() => {
    if (!isClinical) return undefined;
    fetchCases();
    const id = setInterval(fetchCases, POLL_MS);
    return () => clearInterval(id);
  }, [isClinical, fetchCases]);

  // Visible = active cases not currently snoozed
  const now = Date.now();
  const visible = cases.filter((c) => !(snoozed[c.key] && snoozed[c.key] > now));

  // Re-open the modal automatically when a NEW (non-snoozed) case appears.
  useEffect(() => {
    if (visible.length > prevCount.current && visible.length > 0) setDismissedOpen(false);
    prevCount.current = visible.length;
  }, [visible.length]);

  // After the user closes the modal, re-pop in 30s as long as any case is still
  // unhandled — it keeps nagging until an intervention is made for every case.
  useEffect(() => {
    if (!dismissedOpen) return undefined;
    const t = setTimeout(() => setDismissedOpen(false), REDISPLAY_MS);
    return () => clearTimeout(t);
  }, [dismissedOpen]);

  const showModal = isClinical && visible.length > 0 && !dismissedOpen;

  function snooze(c) {
    setSnoozed((s) => ({ ...s, [c.key]: Date.now() + SNOOZE_MS }));
    toast.message(`Snoozed ${c.patient_name} for 10 minutes`);
  }

  function openPatient(c) { navigate(`/clinical/patients/${c.patient_id}`); setDismissedOpen(true); }
  function openSession(c) { navigate(`/hotline/sessions/${c.case_id}`); setDismissedOpen(true); }

  async function run(c, fn, successMsg) {
    setBusyKey(c.key);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      setCases((prev) => prev.filter((x) => x.key !== c.key)); // optimistic removal
      setActionMode((m) => { const n = { ...m }; delete n[c.key]; return n; });
      fetchCases();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  }

  const acknowledge = (c) => run(c, () => api.post(`/rpm/alerts/${c.case_id}/acknowledge`, {}), "Alert acknowledged");
  const startIntervention = (c) => run(c, () => api.post(`/rpm/alerts/${c.case_id}/start`, {}), "Intervention started");

  function beginMode(c, mode) {
    setActionMode((m) => ({ ...m, [c.key]: { mode, note: "" } }));
  }
  function setNote(c, note) {
    setActionMode((m) => ({ ...m, [c.key]: { ...m[c.key], note } }));
  }
  async function confirmMode(c) {
    const am = actionMode[c.key];
    if (!am) return;
    const note = (am.note || "").trim();
    if (am.mode === "escalate") {
      if (!note) return toast.error("Enter an escalation reason");
      if (c.case_type === "rpm_alert") {
        await run(c, () => api.post(`/rpm/alerts/${c.case_id}/escalate`, { reason: note }), "Alert escalated");
      } else {
        await run(c, () => api.post(`/hotline/sessions/${c.case_id}/escalations`, { type: "physician", reason: note, priority: "high" }), "Session escalated");
      }
    } else if (am.mode === "resolve") {
      if (!note) return toast.error("Enter resolution notes");
      await run(c, () => api.post(`/rpm/alerts/${c.case_id}/resolve`, { notes: note }), "Alert resolved");
    }
  }

  return (
    <>
      {children}

      {showModal && (
        <div className="fixed inset-0 z-[9998] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="critical-case-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl border-t-4 border-t-[#B91C1C]"
            style={{ animation: "criticalPulse 1.8s ease-in-out infinite" }}>
            <style>{`@keyframes criticalPulse {0%,100%{box-shadow:0 12px 50px rgba(185,28,28,.20),0 0 0 0 rgba(185,28,28,.35)}50%{box-shadow:0 12px 60px rgba(185,28,28,.30),0 0 0 12px rgba(185,28,28,0)}}`}</style>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#F1F5F9]">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-[#FEE2E2] text-[#B91C1C] flex items-center justify-center animate-pulse">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#B91C1C]">Critical cases requiring intervention</div>
                  <div className="text-sm font-bold text-[#1F2937]" data-testid="critical-case-count">{visible.length} active case{visible.length > 1 ? "s" : ""}</div>
                </div>
              </div>
              <button onClick={() => setDismissedOpen(true)} className="w-8 h-8 rounded-md hover:bg-[#F5F7FA] flex items-center justify-center" data-testid="critical-modal-close" title="Hide — re-appears in 30s while any case is unhandled">
                <X className="w-4 h-4 text-[#6B7280]" />
              </button>
            </div>

            <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
              {visible.map((c) => {
                const am = actionMode[c.key];
                const isRpm = c.case_type === "rpm_alert";
                const busy = busyKey === c.key;
                return (
                  <div key={c.key} className="border border-[#E5E7EB] rounded-xl p-3.5" data-testid={`critical-case-${c.key}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isRpm ? "bg-[#FEF2F2] text-[#B91C1C]" : "bg-[#EFF6FF] text-[#1D4ED8]"}`}>
                        {isRpm ? <HeartPulse className="w-5 h-5" /> : <Phone className="w-5 h-5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#1F2937]">{c.patient_name}</span>
                          {c.mrn && <span className="text-xs font-mono text-[#94A3B8]"><CopyableId value={c.mrn} kind="MRN" /></span>}
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C] font-bold">{isRpm ? "RPM Critical" : `Hotline ${c.priority || "critical"}`}</span>
                          <span className="text-[11px] text-[#94A3B8] flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(c.created_at)}</span>
                        </div>
                        <div className="text-sm text-[#1F2937] mt-0.5">{c.title}</div>
                        {c.detail && <div className="text-xs text-[#6B7280] capitalize">{c.detail}</div>}
                        {c.record_id && <div className="text-[11px] font-mono text-[#0B7E8C] mt-0.5">{c.record_id}</div>}
                      </div>
                    </div>

                    {am ? (
                      <div className="mt-3 space-y-2" data-testid={`critical-mode-${c.key}`}>
                        <Textarea rows={2} autoFocus
                          placeholder={am.mode === "escalate" ? "Escalation reason…" : "Resolution / intervention notes…"}
                          value={am.note} onChange={(e) => setNote(c, e.target.value)} data-testid={`critical-note-${c.key}`} />
                        <div className="flex items-center gap-2 justify-end">
                          <Button size="sm" variant="outline" className="border-[#E5E7EB]" onClick={() => setActionMode((m) => { const n = { ...m }; delete n[c.key]; return n; })}>Cancel</Button>
                          <Button size="sm" disabled={busy} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" onClick={() => confirmMode(c)} data-testid={`critical-confirm-${c.key}`}>
                            {am.mode === "escalate" ? "Escalate" : "Resolve"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-wrap mt-3">
                        {isRpm && (
                          <>
                            <Button size="sm" variant="outline" disabled={busy} className="h-8 border-[#E5E7EB]" onClick={() => acknowledge(c)} data-testid={`critical-ack-${c.key}`}><Check className="w-3.5 h-3.5 mr-1" />Acknowledge</Button>
                            <Button size="sm" variant="outline" disabled={busy} className="h-8 border-[#E5E7EB]" onClick={() => startIntervention(c)} data-testid={`critical-start-${c.key}`}><Play className="w-3.5 h-3.5 mr-1" />Start intervention</Button>
                            <Button size="sm" variant="outline" disabled={busy} className="h-8 border-[#F59E0B] text-[#B45309]" onClick={() => beginMode(c, "escalate")} data-testid={`critical-escalate-${c.key}`}><ArrowUpRight className="w-3.5 h-3.5 mr-1" />Escalate</Button>
                            <Button size="sm" variant="outline" disabled={busy} className="h-8 border-[#15803D] text-[#15803D]" onClick={() => beginMode(c, "resolve")} data-testid={`critical-resolve-${c.key}`}><Check className="w-3.5 h-3.5 mr-1" />Resolve</Button>
                            <Button size="sm" variant="outline" className="h-8 border-[#E5E7EB]" onClick={() => openPatient(c)} data-testid={`critical-open-${c.key}`}><ExternalLink className="w-3.5 h-3.5 mr-1" />Open patient</Button>
                          </>
                        )}
                        {!isRpm && (
                          <>
                            <Button size="sm" variant="outline" className="h-8 border-[#E5E7EB]" onClick={() => openSession(c)} data-testid={`critical-open-${c.key}`}><ExternalLink className="w-3.5 h-3.5 mr-1" />Open session</Button>
                            <Button size="sm" variant="outline" disabled={busy} className="h-8 border-[#F59E0B] text-[#B45309]" onClick={() => beginMode(c, "escalate")} data-testid={`critical-escalate-${c.key}`}><ArrowUpRight className="w-3.5 h-3.5 mr-1" />Escalate</Button>
                          </>
                        )}
                        <Button size="sm" className="h-8 bg-[#B91C1C] hover:bg-[#991B1B] text-white" onClick={() => setSos({ open: true, context: { case_type: c.case_type, case_id: c.case_id, patient_id: c.patient_id, patient_name: c.patient_name } })} data-testid={`critical-sos-${c.key}`}><Siren className="w-3.5 h-3.5 mr-1" />SOS</Button>
                        <Button size="sm" variant="ghost" className="h-8 text-[#6B7280]" onClick={() => snooze(c)} data-testid={`critical-snooze-${c.key}`}><BellOff className="w-3.5 h-3.5 mr-1" />Snooze 10m</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <SosDialog open={sos.open} onOpenChange={(o) => setSos((s) => ({ ...s, open: o }))} context={sos.context}
        onDone={() => fetchCases()} />
    </>
  );
}
